/**
 * Display-only helpers — bigint nano-TON ↔ Number / percentage.
 *
 * Why this file exists
 * ────────────────────
 *   The Home feed bug report (CC-025 follow-up): every card showed
 *   "0 / 1500 TON" with a 0% progress bar, even on a curve with
 *   realTonReserves = 99_000_000n (= 0.099 TON, ~10% of a 1 TON
 *   target). Two bugs combined:
 *
 *     1. `Number(BigInt(reserves) / 1_000_000_000n)` did the
 *        division in BigInt space first, truncating 99_000_000n to
 *        0n. Any sub-TON value collapsed to 0 in the UI.
 *     2. The target was hardcoded `1500` — a stale guess from a
 *        prod-targetTon = 500 era, now wrong for both prod (500)
 *        and dev (1).
 *
 *   Both bugs are display-only — the API was returning the right
 *   raw numbers all along. This module isolates the two correct
 *   conversions so every place that renders curve fill uses the
 *   same code path.
 *
 * Numeric precision note
 * ──────────────────────
 *   `nanoToTonDisplay(n)` converts via a Number intermediate. JS
 *   Number is exact up to 2^53 = 9_007_199_254_740_992. For TON
 *   denominated in nano, that's 9 million TON — well above any
 *   plausible curve size (cohort-1 cap is 1000 TON per token,
 *   factory TVL cap 5000). `progressPct` does its multiply in BigInt
 *   space first to avoid intermediate Number rounding, then converts.
 */

/**
 * Convert nano-TON (bigint) to a display string, trailing zeros trimmed.
 *
 * Use ONLY for rendering — don't feed the result back into contract
 * math. Display precision is capped at 6 decimal places (invisible to
 * humans past that); a whole number renders without a decimal tail
 * ("1", not "1.000") and a fractional one keeps only its significant
 * digits ("0.15", not "0.150").
 *
 * Examples:
 *   nanoToTonDisplay(0n)                 → '0'
 *   nanoToTonDisplay(1_000_000_000n)     → '1'
 *   nanoToTonDisplay(99_000_000n)        → '0.099'
 *   nanoToTonDisplay(12_500_000_000n)    → '12.5'
 *   nanoToTonDisplay(5_000_000_000_000n) → '5000'
 */
export function nanoToTonDisplay(n: bigint): string {
  const ton = Number(n) / 1_000_000_000;
  // toFixed(6) caps display precision; parseFloat strips the trailing
  // zeros (and the decimal point entirely for whole numbers).
  return parseFloat(ton.toFixed(6)).toString();
}

/**
 * Compute "% filled" of a bonding curve toward its target.
 *
 * Always returns a Number in [0, 100]. Edge cases:
 *   - target <= 0   → 0  (degenerate, shouldn't happen with real
 *                       deployments; defensive).
 *   - real <= 0     → 0  (curve untouched).
 *   - real >= target → 100 (graduated or rounding tip-over).
 *
 * Intermediate math runs in BigInt (`real * 10000n / target`)
 * before the Number conversion so we don't lose 1-bps of precision
 * to early floating-point rounding.
 *
 * Examples:
 *   progressPct(99_000_000n, 1_000_000_000n)   → 9.9
 *   progressPct(500_000_000n, 1_000_000_000n)  → 50
 *   progressPct(1_001_000_000n, 1_000_000_000n) → 100  (clamped)
 */
/**
 * CC-037 — estimate the TON a seller receives for `jettonIn` jettons.
 *
 * Mirrors the bonding curve's `receive(JettonTransferNotification)`
 * (bonding_curve.tact): constant-product swap in the sell direction,
 * minus a 1% fee on the gross output. All amounts in nano.
 *
 * This is a DISPLAY estimate only ("получишь примерно X TON"). The real
 * payout is computed on-chain and shown exactly by the wallet preview;
 * the sell transaction carries `minTonOut = 0`, so a stale or slightly
 * off estimate never blocks a sell. (The API currently serves an
 * approximate `virtualTon`, so treat the number as indicative.)
 *
 * Returns 0n for non-positive / degenerate inputs (defensive).
 */
export function quoteSellTonOut(opts: {
  jettonIn: bigint;
  realTon: bigint;
  realJetton: bigint;
  virtualTon: bigint;
  virtualJetton: bigint;
}): bigint {
  const { jettonIn, realTon, realJetton, virtualTon, virtualJetton } = opts;
  if (jettonIn <= 0n) return 0n;
  const totalTon = virtualTon + realTon;
  const totalJetton = virtualJetton + realJetton;
  if (totalTon <= 0n || totalJetton <= 0n) return 0n;
  const k = totalTon * totalJetton;
  const newTotalJetton = totalJetton + jettonIn;
  const newTotalTon = k / newTotalJetton;
  const tonGross = totalTon - newTotalTon;
  if (tonGross <= 0n) return 0n;
  const fee = (tonGross * 100n) / 10_000n; // 1% — FEE_BPS / BPS_DENOM
  return tonGross - fee;
}

export function progressPct(real: bigint, target: bigint): number {
  if (target <= 0n) return 0;
  if (real <= 0n) return 0;
  if (real >= target) return 100;
  // Multiply by 10000 (4 decimal places of bps) before dividing,
  // so the result has 2 decimal places of percent precision.
  const bps = Number((real * 10_000n) / target);
  return bps / 100;
}

/** Gas the curve subtracts from incoming TON BEFORE the fee/curve math.
 *  MUST match SWAP_GAS_RESERVE in bonding_curve.tact (0.05 TON). */
const SWAP_GAS_RESERVE = 50_000_000n;
// After the 3% cohort-4 fee, 97% of post-gas tonIn reaches the curve.
const FEE_FACTOR_NUM = 100n;
const FEE_FACTOR_DEN = 97n;
// Extra over-send margin for snapshot drift; the contract clamps + refunds.
const SAFETY_MARGIN_NUM = 105n;
const SAFETY_MARGIN_DEN = 100n;

/**
 * CC-032 / CC-052-G: the wallet-ask TON nanoamount the Buy tab's "MAX"
 * button fills in.
 *
 * `remainingNano` is `targetTon − realTonReserves` — the TON that must
 * actually land in the curve to graduate it. The wallet-ask value is
 * LARGER than that, because the contract `receive(Buy)` first subtracts
 * `SWAP_GAS_RESERVE` (0.05 TON) and then a 3% protocol fee before the
 * curve math runs:
 *
 *   walletValue = (remainingNano / 0.97 + SWAP_GAS_RESERVE) × 1.05
 *
 * CC-052-G hotfix: the previous version was just `remainingNano × 1.05`.
 * That is mathematically broken near graduation — a typical remaining of
 * ~0.035 TON gave a 5% buffer of ~0.0018 TON, 28× too small to cover the
 * fixed 0.05 TON gas reserve. The contract computed `tonIn = value −
 * 0.05 < 0`, hit `require(tonIn > 0)`, bounced, and 100%-refunded — so
 * MAX could never graduate a curve. This version adds the gas reserve as
 * an absolute term and grosses up for the fee, then applies the 5%
 * safety margin. Over-send lands in the contract's clamp branch
 * (CC-052-A) which refunds the excess — it never under-fills.
 *
 * BigInt-only — integer division keeps the math exact (no float).
 *
 * Examples:
 *   maxBuyNano(0n)               → 0n
 *   maxBuyNano(1_000_000_000n)   → 1_134_974_226n
 *   maxBuyNano(35_300_000n)      → 90_711_339n   (near-graduation case)
 */
export function maxBuyNano(remainingNano: bigint): bigint {
  if (remainingNano <= 0n) return 0n;
  const tonInNeeded = (remainingNano * FEE_FACTOR_NUM) / FEE_FACTOR_DEN;
  const withGasReserve = tonInNeeded + SWAP_GAS_RESERVE;
  return (withGasReserve * SAFETY_MARGIN_NUM) / SAFETY_MARGIN_DEN;
}

/**
 * CC-052-E: render a jetton base-unit amount (bigint or stringified
 * BigInt) as a human-readable decimal string.
 *
 * Display-only — don't feed the result back into contract math. Divides
 * by `10n ** BigInt(decimals)`; returns the whole part alone when there's
 * no fraction, else `whole.fracStr` with trailing zeros trimmed.
 *
 * Examples (decimals = 9):
 *   formatJetton(0n, 9)               → '0'
 *   formatJetton(1_000_000_000n, 9)   → '1'
 *   formatJetton(1_500_000_000n, 9)   → '1.5'
 *   formatJetton(1_234_567_000n, 9)   → '1.234567'
 */
export function formatJetton(nano: string | bigint, decimals: number): string {
  const value = typeof nano === 'bigint' ? nano : BigInt(nano);
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const frac = value % divisor;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toString()}.${fracStr}`;
}
